import escapeStringRegexp from 'escape-string-regexp'

import { BASE_BREAKPOINT, BREAKPOINTS } from 'views/consts/imgBreakpoints'

import {
  addIsRequiredChecker,
  chainCustomCheckers,
  getPropDescriptor,
} from './helpers'

const SRC_SET_SIZE_DESCRIPTOR_UNITS = ['w']
const SIZE_UNITS = ['vw', 'em', 'rem', 'px']

function createNumberWithUnitsRegExpString(
  unitsRegExpStrings: Array<string>
): string {
  return `[0-9]+(?:\\.[0-9]*)?(?:${unitsRegExpStrings.join('|')})`
}

function validateObject(
  props: Record<string, unknown>,
  propName: string,
  componentName: string,
  location: string,
  propFullName: string
): Error | undefined {
  const preciseType = typeof props[propName]

  if (preciseType === 'undefined') {
    return undefined
  }

  if (preciseType !== 'object') {
    const propDesc = getPropDescriptor(
      propName,
      componentName,
      location,
      propFullName
    )
    return new TypeError(
      `The ${propDesc} got invalid type \`${preciseType}\`, expected \`object\`.`
    )
  }

  return undefined
}

const validateSrcSet = (
  props: Record<string, unknown>,
  propName: string,
  componentName: string,
  location: string,
  propFullName: string
): Error | undefined => {
  const srcSetObject = props[propName]

  if (srcSetObject == null || typeof srcSetObject !== 'object') {
    return undefined
  }

  const keyRegExp = new RegExp(
    `^${createNumberWithUnitsRegExpString(
      SRC_SET_SIZE_DESCRIPTOR_UNITS.map(escapeStringRegexp)
    )}$`
  )

  const keys = Object.keys(srcSetObject)
  const unexpectedKeys = keys.filter(
    key => !keyRegExp.exec(String(key).trim().toLowerCase())
  )
  if (unexpectedKeys.length !== 0) {
    const propDesc = getPropDescriptor(
      propName,
      componentName,
      location,
      propFullName
    )
    return new TypeError(
      `Unexpected keys found in ${propDesc}: ${unexpectedKeys}. Allowed units are: ${SRC_SET_SIZE_DESCRIPTOR_UNITS}`
    )
  }

  if (keys.length - unexpectedKeys.length === 0) {
    const propDesc = getPropDescriptor(
      propName,
      componentName,
      location,
      propFullName
    )
    return new TypeError(`No keys found in ${propDesc}`)
  }

  return undefined
}

const validateSizes = (
  props: Record<string, unknown>,
  propName: string,
  componentName: string,
  location: string,
  propFullName: string
): Error | undefined => {
  const sizesObject = props[propName]

  if (sizesObject == null || typeof sizesObject !== 'object') {
    return undefined
  }

  if (!(BASE_BREAKPOINT in sizesObject)) {
    const propDesc = getPropDescriptor(
      propName,
      componentName,
      location,
      propFullName
    )
    return new TypeError(`Key \`${BASE_BREAKPOINT}\` not found in ${propDesc}.`)
  }

  const allowedKeys = [BASE_BREAKPOINT, ...BREAKPOINTS]
  const unexpectedKeys = Object.keys(sizesObject).filter(
    key => !allowedKeys.includes(key)
  )
  if (unexpectedKeys.length !== 0) {
    const propDesc = getPropDescriptor(
      propName,
      componentName,
      location,
      propFullName
    )
    return new TypeError(
      `Unexpected keys found in ${propDesc}: ${unexpectedKeys}`
    )
  }

  // Values might be CSS values with units or a calc() expression.
  // For calc() we find everything that looks like a CSS value and check
  // it’s unit.

  const potencialValuesGlobalRegExp = new RegExp(
    createNumberWithUnitsRegExpString(['[a-z%]+']),
    'g'
  )
  const valueRegExp = new RegExp(
    `^${createNumberWithUnitsRegExpString(SIZE_UNITS.map(escapeStringRegexp))}$`
  )
  const unexpectedValues = Object.values(sizesObject).filter(size => {
    const trimmedSize = String(size).trim().toLowerCase()

    if (!trimmedSize.startsWith('calc(')) {
      return !valueRegExp.exec(trimmedSize)
    }

    const potencialValues = trimmedSize.match(potencialValuesGlobalRegExp)
    if (!potencialValues) {
      return false
    }
    return potencialValues.some(value => !valueRegExp.exec(value))
  })
  if (unexpectedValues.length !== 0) {
    const propDesc = getPropDescriptor(
      propName,
      componentName,
      location,
      propFullName
    )
    return new TypeError(
      `Unexpected values found in ${propDesc}: ${unexpectedValues}. Allowed units are: ${SIZE_UNITS}`
    )
  }

  return undefined
}

const srcSetPropType = addIsRequiredChecker(
  chainCustomCheckers([validateObject, validateSrcSet])
)

const sizesPropType = addIsRequiredChecker(
  chainCustomCheckers([validateObject, validateSizes])
)

export { srcSetPropType, sizesPropType }
